import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth-context";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUpload } from "@/components/common/ImageUpload";

export default function BecomeVendor() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const cats = useQuery({ queryKey: ["categories"], queryFn: () => api("/categories") });
  const [form, setForm] = useState({
    name: "",
    category: "",
    description: "",
    phone: "",
    whatsapp_number: "",
    logo_url: null,
    banner_url: null,
    latitude: -33.9321,
    longitude: 18.8602,
    delivery_fee_base: 25,
    eta_minutes: 30,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category) {
      toast.error("Store name and category are required");
      return;
    }
    setBusy(true);
    try {
      await api("/vendors", {
        method: "POST",
        json: {
          ...form,
          latitude: Number(form.latitude),
          longitude: Number(form.longitude),
          delivery_fee_base: Number(form.delivery_fee_base),
          eta_minutes: Number(form.eta_minutes),
        },
      });
      await refresh();
      toast.success("Store submitted for approval!");
      navigate("/vendor", { replace: true });
    } catch (err) {
      toast.error(err.message || "Could not create store");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="font-display text-2xl">Open your store</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Tell us about your business. An admin will review and approve it.
      </p>
      <form onSubmit={submit} className="mt-6 space-y-5">
        <Card className="space-y-4 rounded-2xl p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Store name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="vendor-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="vendor-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {cats.data?.map((c) => (
                    <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="vendor-description-input" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+27..." />
            </div>
            <div className="space-y-1.5">
              <Label>WhatsApp</Label>
              <Input value={form.whatsapp_number} onChange={(e) => set("whatsapp_number", e.target.value)} placeholder="+27..." />
            </div>
          </div>
        </Card>

        <Card className="grid gap-4 rounded-2xl p-5 sm:grid-cols-2">
          <div>
            <Label className="mb-2 block">Logo</Label>
            <ImageUpload value={form.logo_url} onChange={(v) => set("logo_url", v)} label="Add logo" aspect="aspect-square" testId="vendor-logo-upload" />
          </div>
          <div>
            <Label className="mb-2 block">Banner</Label>
            <ImageUpload value={form.banner_url} onChange={(v) => set("banner_url", v)} label="Add banner" testId="vendor-banner-upload" />
          </div>
        </Card>

        <Card className="grid gap-4 rounded-2xl p-5 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Latitude</Label>
            <Input type="number" step="any" value={form.latitude} onChange={(e) => set("latitude", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Longitude</Label>
            <Input type="number" step="any" value={form.longitude} onChange={(e) => set("longitude", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Delivery fee (R)</Label>
            <Input type="number" value={form.delivery_fee_base} onChange={(e) => set("delivery_fee_base", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>ETA (min)</Label>
            <Input type="number" value={form.eta_minutes} onChange={(e) => set("eta_minutes", e.target.value)} />
          </div>
        </Card>

        <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="submit-vendor-button">
          {busy ? "Submitting…" : "Submit for approval"}
        </Button>
      </form>
    </div>
  );
}
